import Vue from 'vue'

class PendingCdrRepository {

  /**
   * Search pending CDRs with filters and pagination
   * POST /charging/pending-cdrs
   */
  search(data = {}) {
    return Vue.auth.fetch({
      url: '/api/charging/pending-cdrs',
      method: 'post',
      data,
    }).then(res => res.data)
  }

  /**
   * Get available issue codes
   * GET /charging/pending-cdrs/issue-codes
   */
  getIssueCodes() {
    return Vue.auth.fetch({
      url: '/api/charging/pending-cdrs/issue-codes',
      method: 'get',
    }).then(res => res.data)
  }

  /**
   * Get correction context for a specific pending CDR
   * GET /charging/pending-cdrs/{id}/correction-context
   */
  getCorrectionContext(id) {
    return Vue.auth.fetch({
      url: `/api/charging/pending-cdrs/${encodeURIComponent(id)}/correction-context`,
      method: 'get',
    }).then(res => res.data)
  }

  /**
   * Submit correction for a pending CDR
   * POST /charging/pending-cdrs/{id}/correct
   */
  correct(id, data) {
    return Vue.auth.fetch({
      url: `/api/charging/pending-cdrs/${encodeURIComponent(id)}/correct`,
      method: 'post',
      data,
    }).then(res => res.data)
  }

  /**
   * Cancel a pending CDR
   * POST /charging/pending-cdrs/{id}/cancel
   */
  cancel(id, reason) {
    return Vue.auth.fetch({
      url: `/api/charging/pending-cdrs/${encodeURIComponent(id)}/cancel`,
      method: 'post',
      data: { reason },
    }).then(res => res.data)
  }

  /**
   * Bulk cancel multiple pending CDRs
   * POST /charging/pending-cdrs/bulk-cancel
   */
  bulkCancel(ids, reason) {
    return Vue.auth.fetch({
      url: '/api/charging/pending-cdrs/bulk-cancel',
      method: 'post',
      data: { ids, reason },
    }).then(res => res.data)
  }

  /**
   * Preview price calculation with modified values
   * POST /charging/pending-cdrs/{id}/preview-price
   * @param {string} id - Pending CDR ID
   * @param {Object} params - Modified values (only changed fields)
   * @param {number} [params.startMeter] - Start meter in Wh
   * @param {number} [params.stopMeter] - Stop meter in Wh
   * @param {string} [params.startTime] - ISO 8601 datetime
   * @param {string} [params.stopTime] - ISO 8601 datetime
   * @param {string} [params.tariffId] - Tariff ID
   */
  previewPrice(id, params) {
    return Vue.auth.fetch({
      url: `/api/charging/pending-cdrs/${encodeURIComponent(id)}/preview-price`,
      method: 'post',
      data: params,
    }).then(res => res.data)
  }

  // ============================================
  // Dashboard & Statistics API
  // ============================================

  /**
   * Get actions required counts (pending CDRs, failed payments, etc.)
   * GET /dashboard/actions-required
   */
  getActionsRequired() {
    return Vue.auth.fetch({
      url: '/api/dashboard/actions-required',
      method: 'get',
    }).then(res => res.data)
  }

  /**
   * Get validation statistics
   * GET /charging/cdr-validation/stats
   * @param {Object} params
   * @param {string} [params.period] - Period: '24h', '7d', '30d', 'custom'
   * @param {boolean} [params.byDay] - Group by day for chart
   * @param {string} [params.dateFrom] - Start date for custom period
   * @param {string} [params.dateTo] - End date for custom period
   * @param {string[]} [params.sources] - Filter by sources
   */
  getStats(params = {}) {
    return Vue.auth.fetch({
      url: '/api/charging/cdr-validation/stats',
      method: 'get',
      data: params,
    }).then(res => res.data)
  }

  /**
   * Get top validation issues
   * GET /charging/cdr-validation/top-issues
   * @param {Object} params
   * @param {string} [params.groupBy] - Group by: 'code', 'evse', 'source'
   * @param {number} [params.limit] - Max results (default 10)
   * @param {string} [params.period] - Period filter
   */
  getTopIssues(params = {}) {
    return Vue.auth.fetch({
      url: '/api/charging/cdr-validation/top-issues',
      method: 'get',
      data: { limit: 10, groupBy: 'code', ...params },
    }).then(res => res.data)
  }

  /**
   * Get current backlog count
   * GET /charging/cdr-validation/backlog
   */
  getBacklog() {
    return Vue.auth.fetch({
      url: '/api/charging/cdr-validation/backlog',
      method: 'get',
    }).then(res => res.data)
  }

  // ============================================
  // Validation Logs API
  // ============================================

  /**
   * Search validation logs (audit trail)
   * POST /charging/cdr-validation-logs
   * @param {Object} params
   * @param {number} [params.offset]
   * @param {number} [params.limit]
   * @param {string[]} [params.statuses] - Filter by status
   * @param {string[]} [params.sources] - Filter by source
   * @param {string[]} [params.decisions] - Filter by decision
   * @param {string} [params.dateFrom]
   * @param {string} [params.dateTo]
   * @param {string} [params.query] - Search in CDR ID, EVSE ID/UID, station ID, EVCO ID, token UID
   */
  searchValidationLogs(params = {}) {
    return Vue.auth.fetch({
      url: '/api/charging/cdr-validation-logs',
      method: 'post',
      data: params,
    }).then(res => res.data)
  }

  /**
   * Get validation history for a specific CDR
   * GET /charging/cdrs/{cdrId}/validation-history
   * @param {string} cdrId
   */
  getCdrValidationHistory(cdrId) {
    return Vue.auth.fetch({
      url: `/api/charging/cdrs/${encodeURIComponent(cdrId)}/validation-history`,
      method: 'get',
    }).then(res => res.data)
  }

  /**
   * Get validation context for any CDR (validated or pending)
   * Works with CDR ID directly, doesn't require PendingCdr to exist
   * GET /charging/cdrs/{cdrId}/validation-context
   * @param {string} cdrId - The CDR ID
   * @returns {Object} Validation context with:
   *   - cdr: CDR data (EVSE, token, pricing)
   *   - adjacentCdrs: Previous/next CDRs for meter continuity
   *   - anomalies: Detected anomalies
   *   - validationHistory: From CdrValidationLog
   *   - correctionHistory: From CdrCorrectionLog
   *   - session: Metrics, events, periods
   *   - tariffOptions: Available tariffs
   */
  getCdrValidationContext(cdrId) {
    return Vue.auth.fetch({
      url: `/api/charging/cdrs/${encodeURIComponent(cdrId)}/validation-context`,
      method: 'get',
    }).then(res => res.data)
  }

  // ============================================
  // CDR Revisions API (Operator Corrections)
  // ============================================

  /**
   * Get revisions for a specific CDR
   * GET /charging/cdrs/{cdrId}/revisions
   * @param {string} cdrId
   */
  getCdrRevisions(cdrId) {
    return Vue.auth.fetch({
      url: `/api/charging/cdrs/${encodeURIComponent(cdrId)}/revisions`,
      method: 'get',
    }).then(res => res.data)
  }

  /**
   * Apply a revision to a CDR
   * POST /charging/cdrs/{cdrId}/revisions/{revisionId}/apply
   * @param {string} cdrId
   * @param {string} revisionId
   */
  applyRevision(cdrId, revisionId) {
    return Vue.auth.fetch({
      url: `/api/charging/cdrs/${encodeURIComponent(cdrId)}/revisions/${encodeURIComponent(revisionId)}/apply`,
      method: 'post',
    }).then(res => res.data)
  }

  /**
   * Reject a revision
   * POST /charging/cdrs/{cdrId}/revisions/{revisionId}/reject
   * @param {string} cdrId
   * @param {string} revisionId
   * @param {string} reason - Reason for rejection
   */
  rejectRevision(cdrId, revisionId, reason) {
    return Vue.auth.fetch({
      url: `/api/charging/cdrs/${encodeURIComponent(cdrId)}/revisions/${encodeURIComponent(revisionId)}/reject`,
      method: 'post',
      data: { reason },
    }).then(res => res.data)
  }

  // ============================================
  // CDR Detail View API
  // ============================================

  /**
   * Get complete CDR detail for the universal detail view
   * GET /api/charging/cdrs/{cdrId}/detail
   * @param {string} cdrId - The CDR ID (UUID)
   * @returns {Object} Complete CDR data for all tabs
   */
  getCdrDetail(cdrId) {
    return Vue.auth.fetch({
      url: `/api/charging/cdrs/${encodeURIComponent(cdrId)}/detail`,
      method: 'get',
    }).then(res => res.data)
  }

}

export default new PendingCdrRepository()
